import type {
  CreateRequirementCategoryInput,
  UpdateRequirementCategoryInput,
  CreateRequirementStatusInput,
  UpdateRequirementStatusInput,
  CreateRequirementInput,
  UpdateRequirementInput,
  ListRequirementsQuery,
  DataResponse,
  PaginatedResponse,
} from '@pms/shared';
import { api } from '@/lib/api';

// ── Response types ────────────────────────────────────────────────────────────

export interface RequirementCategory {
  id: string;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  requirements_count: number;
  created_at: string;
  updated_at: string;
}

export interface RequirementStatus {
  id: string;
  name: string;
  code: string;
  color: string;
  display_order: number;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RequirementListItem {
  id: string;
  title: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  quantity: number;
  unit: string;
  estimated_cost: number | null;
  approved_budget: number | null;
  due_date: string | null;
  fulfilled_date: string | null;
  created_at: string;
  updated_at: string;
  category: { id: string; name: string } | null;
  status: { id: string; name: string; code: string; color: string } | null;
  requested_by: { id: string; name: string } | null;
  assigned_to: { id: string; name: string } | null;
}

export interface RequirementComment {
  id: string;
  requirement_id: string;
  user_id: string;
  user_name: string;
  body: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RequirementAuditEntry {
  id: string;
  requirement_id: string;
  changed_by: string;
  changed_by_name: string;
  action: string;
  field_name: string | null;
  old_value: string | null;
  new_value: string | null;
  note: string | null;
  created_at: string;
}

export interface RequirementDetail extends RequirementListItem {
  description: string | null;
  rejection_reason: string | null;
  notes: string | null;
  comments: RequirementComment[];
  audit_log: RequirementAuditEntry[];
}

export interface RequirementStats {
  total: number;
  pending_approval: number;
  by_status: Array<{
    status_id: string;
    status_name: string;
    status_code: string;
    color: string;
    count: number;
  }>;
  by_category: Array<{
    category_id: string;
    category_name: string;
    count: number;
  }>;
  by_priority: Array<{ priority: string; count: number }>;
  recent: RequirementListItem[];
}

// ── Service ───────────────────────────────────────────────────────────────────

export const requirementsService = {
  // Stats
  async getStats(): Promise<RequirementStats> {
    const res = await api.get<DataResponse<RequirementStats>>('/requirements/stats');
    return res.data.data;
  },

  // Categories
  async listCategories(): Promise<RequirementCategory[]> {
    const res = await api.get<DataResponse<RequirementCategory[]>>('/requirements/categories');
    return res.data.data;
  },

  async createCategory(input: CreateRequirementCategoryInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/requirements/categories', input);
    return res.data.data;
  },

  async updateCategory(id: string, input: UpdateRequirementCategoryInput): Promise<void> {
    await api.patch(`/requirements/categories/${id}`, input);
  },

  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/requirements/categories/${id}`);
  },

  // Statuses
  async listStatuses(): Promise<RequirementStatus[]> {
    const res = await api.get<DataResponse<RequirementStatus[]>>('/requirements/statuses');
    return res.data.data;
  },

  async createStatus(input: CreateRequirementStatusInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/requirements/statuses', input);
    return res.data.data;
  },

  async updateStatus(id: string, input: UpdateRequirementStatusInput): Promise<void> {
    await api.patch(`/requirements/statuses/${id}`, input);
  },

  async deleteStatus(id: string): Promise<void> {
    await api.delete(`/requirements/statuses/${id}`);
  },

  // Requirements CRUD
  async listRequirements(
    query: Partial<ListRequirementsQuery> = {},
  ): Promise<PaginatedResponse<RequirementListItem>> {
    const res = await api.get<PaginatedResponse<RequirementListItem>>('/requirements', {
      params: query,
    });
    return res.data;
  },

  async getRequirement(id: string): Promise<RequirementDetail> {
    const res = await api.get<DataResponse<RequirementDetail>>(`/requirements/${id}`);
    return res.data.data;
  },

  async createRequirement(input: CreateRequirementInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/requirements', input);
    return res.data.data;
  },

  async updateRequirement(id: string, input: UpdateRequirementInput): Promise<void> {
    await api.patch(`/requirements/${id}`, input);
  },

  async deleteRequirement(id: string): Promise<void> {
    await api.delete(`/requirements/${id}`);
  },

  async updateRequirementStatus(
    id: string,
    statusId: string,
    rejectionReason?: string | null,
  ): Promise<void> {
    await api.patch(`/requirements/${id}/status`, {
      status_id: statusId,
      rejection_reason: rejectionReason ?? null,
    });
  },

  // Comments
  async addComment(requirementId: string, body: string): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>(
      `/requirements/${requirementId}/comments`,
      { body },
    );
    return res.data.data;
  },

  async deleteComment(commentId: string): Promise<void> {
    await api.delete(`/requirements/comments/${commentId}`);
  },
};
